import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api, API_URL, resolveMediaUrl } from '../../utils/api';
import ProtectedRoute from '../../components/ProtectedRoute';
import AdminLayout, { useTheme } from '../../components/AdminLayout';
import { useNotification } from '../../contexts/NotificationContext';

/* ─────────────────────────────────────────────────────────────────────────
   Closed Dates Modal
───────────────────────────────────────────────────────────────────────── */
function ClosedDatesModal({ branch, onClose }) {
  const { t } = useTranslation();
  const { showAlert, showConfirm } = useNotification();
  const { T, S } = useTheme();
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ date: '', reason: 'Holiday' });

  const fetchDates = useCallback(async () => {
    try {
      const data = await api.getBranchClosedDates(branch.id);
      setDates(data.sort((a, b) => a.date.localeCompare(b.date)));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [branch.id]);

  useEffect(() => { fetchDates(); }, [fetchDates]);

  const add = async () => {
    if (!form.date) return showAlert(t('admin.selectDate'));
    try {
      await api.createClosedDate(branch.id, form);
      setForm({ ...form, date: '' });
      fetchDates();
    } catch (err) { showAlert(err.message); }
  };

  const remove = async (id) => {
    showConfirm(t('admin.removeClosedDateConfirm'), async () => {
      try { await api.deleteClosedDate(id); fetchDates(); }
      catch (err) { showAlert(err.message); }
    });
  };

  return (
    <div style={S.modal}>
      <div style={{ ...S.modalBox, maxWidth: '500px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={S.modalTitle}>{t('admin.closedDatesTitle', { name: branch.name })}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.muted, cursor: 'pointer', fontSize: '1.2rem' }}>×</button>
        </div>

        {/* Add Form */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: '0.75rem', color: T.muted, marginBottom: '0.3rem' }}>{t('admin.date')}</label>
            <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} style={S.input} />
          </div>
          <div style={{ flex: 1.5 }}>
            <label style={{ display: 'block', fontSize: '0.75rem', color: T.muted, marginBottom: '0.3rem' }}>{t('admin.reason')}</label>
            <input placeholder={t('admin.reasonPlaceholder')} value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} style={S.input} />
          </div>
          <button onClick={add} style={{ ...S.btnOrange, padding: '0.6rem 1rem', height: '42px' }}>{t('common.add')}</button>
        </div>

        {/* List */}
        {loading ? <p style={{ color: T.muted }}>{t('common.loading')}</p> : dates.length === 0 ? (
          <p style={{ color: T.muted, fontStyle: 'italic', fontSize: '0.9rem' }}>{t('admin.noClosedDatesSet')}</p>
        ) : (
          <div style={{ maxHeight: '300px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {dates.map(d => (
              <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.03)', padding: '0.7rem', borderRadius: '6px', border: `1px solid ${T.border}` }}>
                <div>
                  <div style={{ fontFamily: "'DM Sans', sans-serif", color: T.white, letterSpacing: '0.05em' }}>{d.date}</div>
                  <div style={{ fontSize: '0.8rem', color: T.muted }}>{d.reason || t('admin.closed')}</div>
                </div>
                <button onClick={() => remove(d.id)} style={{ ...S.btnDanger, padding: '0.3rem 0.6rem', fontSize: '0.75rem' }}>{t('admin.remove')}</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Inline branch CRUD panel for one shop
───────────────────────────────────────────────────────────────────────── */
function BranchPanel({ shopId, allUsers }) {
  const { t } = useTranslation();
  const { showAlert, showConfirm } = useNotification();
  const { T, S } = useTheme();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // 'create' | branch-id
  const [managingDates, setManagingDates] = useState(null); // branch object
  const [form, setForm] = useState({
    name: '',
    address: '',
    manager_id: '',
    working_hours: '',
    social_instagram: '',
    social_facebook: '',
    social_tiktok: '',
  });
  const [uploadingBranchLogo, setUploadingBranchLogo] = useState(null);

  const fetchBranches = useCallback(async () => {
    try {
      const data = await api.getBranches(null, shopId);
      setBranches(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [shopId]);

  useEffect(() => { fetchBranches(); }, [fetchBranches]);

  const openCreate = () => {
    setEditing('create');
    setForm({ name: '', address: '', manager_id: '', working_hours: '', social_instagram: '', social_facebook: '', social_tiktok: '' });
  };

  const openEdit = (b) => {
    const msl = b.manager_social_links || {};
    setEditing(b.id);
    setForm({
      name: b.name,
      address: b.address ?? '',
      manager_id: b.manager_id ?? '',
      working_hours: b.working_hours || '',
      social_instagram: msl.instagram || '',
      social_facebook: msl.facebook || '',
      social_tiktok: msl.tiktok || '',
    });
  };

  const save = async () => {
    try {
      const manager_social_links = {};
      if (form.social_instagram?.trim()) manager_social_links.instagram = form.social_instagram.trim();
      if (form.social_facebook?.trim()) manager_social_links.facebook = form.social_facebook.trim();
      if (form.social_tiktok?.trim()) manager_social_links.tiktok = form.social_tiktok.trim();
      const base = {
        shop_id: shopId,
        name: form.name,
        address: form.address,
        manager_id: form.manager_id ? parseInt(form.manager_id) : null,
      };
      const wh = form.working_hours?.trim() ? form.working_hours.trim() : null;
      if (editing === 'create') {
        const createPayload = { ...base, ...(wh ? { working_hours: wh } : {}) };
        if (Object.keys(manager_social_links).length) createPayload.manager_social_links = manager_social_links;
        await api.createBranch(createPayload);
      } else {
        await api.updateBranch(editing, {
          name: form.name,
          address: form.address,
          manager_id: base.manager_id,
          working_hours: wh,
          manager_social_links: manager_social_links,
        });
      }
      setEditing(null);
      fetchBranches();
    } catch (err) { showAlert(err.message || t('admin.failedSaveBranch')); }
  };

  const remove = async (id) => {
    showConfirm(t('admin.deleteBranchConfirm'), async () => {
      try { await api.deleteBranch(id); fetchBranches(); }
      catch (err) { showAlert(err.message || t('admin.failedDelete')); }
    });
  };

  const onBranchLogoSelect = async (branchId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setUploadingBranchLogo(branchId);
    try {
      await api.uploadBranchLogo(branchId, formData);
      fetchBranches();
    } catch (err) {
      showAlert(err.message || t('admin.uploadFailed'));
    } finally {
      setUploadingBranchLogo(null);
      e.target.value = '';
    }
  };

  const onDeleteBranchLogo = async (branchId) => {
    const branch = branches.find((b) => b.id === branchId);
    if (!branch?.logo_url) return;
    showConfirm(t('admin.deleteLogoConfirm', 'Delete this logo?'), async () => {
      setUploadingBranchLogo(branchId);
      try {
        await api.deleteBranchLogo(branchId);
        fetchBranches();
      } catch (err) {
        showAlert(err.message || t('admin.uploadFailed'));
      } finally {
        setUploadingBranchLogo(null);
      }
    });
  };

  const branchManagers = allUsers.filter(u => u.role === 'branch_manager');

  const panelStyle = {
    background: 'rgba(27,213,173,0.03)',
    border: `1px solid ${T.border}`,
    borderTop: 'none',
    padding: '1.25rem 1.5rem',
  };

  const subTh = { ...S.th, fontSize: '0.68rem', background: 'rgba(27,213,173,0.06)', padding: '0.5rem 0.85rem' };
  const subTd = { ...S.td, fontSize: '0.84rem', padding: '0.55rem 0.85rem' };

  return (
    <div style={panelStyle}>
      {/* Modal for branch create/edit */}
      {editing && (
        <div style={S.modal}>
          <div style={S.modalBox}>
            <h2 style={S.modalTitle}>{editing === 'create' ? t('admin.newBranch') : t('admin.editBranch')}</h2>
            <input placeholder={t('admin.branchName')} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={S.input} />
            <input placeholder={t('admin.address')} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} style={S.input} />
            <label style={{ display: 'block', fontSize: '0.75rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>
              {t('admin.branchManagerOptional')}
            </label>
            <select value={form.manager_id} onChange={e => setForm({ ...form, manager_id: e.target.value })} style={S.select}>
              <option value="">{t('admin.none')}</option>
              {branchManagers.map(u => (
                <option key={u.id} value={u.id}>{u.name} ({u.email}){u.branch_id ? ` [${t('admin.assigned')}]` : ''}</option>
              ))}
            </select>
            <input placeholder={t('admin.workingHoursOptional')} value={form.working_hours} onChange={e => setForm({ ...form, working_hours: e.target.value })} style={S.input} />
            <div style={{ fontSize: '0.72rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: '0.35rem' }}>{t('admin.branchManagerSocialLinks')}</div>
            <input placeholder="Instagram URL" value={form.social_instagram} onChange={e => setForm({ ...form, social_instagram: e.target.value })} style={S.input} />
            <input placeholder="Facebook URL" value={form.social_facebook} onChange={e => setForm({ ...form, social_facebook: e.target.value })} style={S.input} />
            <input placeholder="TikTok URL" value={form.social_tiktok} onChange={e => setForm({ ...form, social_tiktok: e.target.value })} style={S.input} />
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={save} style={{ ...S.btnOrange, flex: 1 }}>{t('common.save')}</button>
              <button onClick={() => setEditing(null)} style={S.btnOutline}>{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Closed Dates Modal */}
      {managingDates && (
        <ClosedDatesModal branch={managingDates} onClose={() => setManagingDates(null)} />
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.85rem' }}>
        <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.7rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: T.muted }}>
          {t('admin.branchesCount')} ({loading ? '…' : branches.length})
        </span>
        <button
          style={{ ...S.btnOrange, fontSize: '0.72rem', padding: '0.3rem 0.9rem' }}
          onClick={openCreate}
          onMouseEnter={e => e.target.style.background = T.orangeHov}
          onMouseLeave={e => e.target.style.background = T.orange}
        >
          + {t('admin.addBranch')}
        </button>
      </div>

      {loading ? (
        <p style={{ color: T.muted, fontSize: '0.82rem' }}>{t('common.loading')}</p>
      ) : branches.length === 0 ? (
        <p style={{ color: T.muted, fontSize: '0.82rem', fontStyle: 'italic' }}>{t('admin.noBranchesYet')}</p>
      ) : (
        <table style={{ ...S.table, fontSize: '0.82rem' }}>
          <thead>
            <tr>
              {[t('admin.id'), t('admin.logo'), t('admin.name'), t('admin.address'), t('admin.manager'), t('admin.actions')].map((h, i) => (
                <th key={i} style={subTh}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {branches.map(b => (
              <tr key={b.id}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(27,213,173,0.06)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <td style={subTd}>{b.id}</td>
                <td style={subTd}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {b.logo_url ? (
                      <img src={resolveMediaUrl(b.logo_url)} alt="" loading="lazy" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6, border: `1px solid ${T.border}` }} />
                    ) : (
                      <div style={{ width: 36, height: 36, borderRadius: 6, background: 'rgba(255,255,255,0.06)', border: `1px dashed ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', color: T.muted }}>—</div>
                    )}
                    <label style={{ cursor: 'pointer', fontSize: '0.7rem', color: T.orange }}>
                      {uploadingBranchLogo === b.id ? '…' : t('admin.upload')}
                      <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => onBranchLogoSelect(b.id, e)} />
                    </label>
                    {b.logo_url && (
                      <button type="button" onClick={() => onDeleteBranchLogo(b.id)} style={{ ...S.btnDanger, padding: '0.2rem 0.45rem', fontSize: '0.68rem' }}>
                        {t('common.delete')}
                      </button>
                    )}
                  </div>
                </td>
                <td style={{ ...subTd, color: T.white, fontWeight: 500 }}>{b.name}</td>
                <td style={{ ...subTd, color: T.muted }}>{b.address || '—'}</td>
                <td style={subTd}>{b.manager_name || '—'}</td>
                <td style={subTd}>
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <button style={{ ...S.btnSmall, fontSize: '0.7rem', padding: '0.25rem 0.65rem' }}
                      onClick={() => setManagingDates(b)}
                      onMouseEnter={e => e.target.style.color = T.orange}
                      onMouseLeave={e => e.target.style.color = T.muted}>
                      {t('admin.closedDates')}
                    </button>
                    <button style={{ ...S.btnSmall, fontSize: '0.7rem', padding: '0.25rem 0.65rem' }}
                      onClick={() => openEdit(b)}
                      onMouseEnter={e => e.target.style.color = T.white}
                      onMouseLeave={e => e.target.style.color = T.muted}>{t('common.edit')}</button>
                    <button style={{ ...S.btnDanger, fontSize: '0.7rem', padding: '0.25rem 0.65rem' }}
                      onClick={() => remove(b.id)}>{t('common.delete')}</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Main Shops page
───────────────────────────────────────────────────────────────────────── */
function ShopsContent() {
  const { t } = useTranslation();
  const { showAlert, showConfirm } = useNotification();
  const { T, S } = useTheme();
  const [shops, setShops] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '',
    category: 'men',
    manager_id: '',
    social_instagram: '',
    social_facebook: '',
    social_tiktok: '',
  });
  const [expandedShop, setExpandedShop] = useState(null); // shop id whose branches are shown
  const [uploadingShopLogo, setUploadingShopLogo] = useState(null);

  const fetchShops = async () => {
    try { const data = await api.getAdminShops(); setShops(data); }
    catch (err) { showAlert(err?.message || t('admin.failedLoad')); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchShops();
    api.getAdminUsers().then(setUsers).catch(() => { /* users optional for manager dropdown */ });
  }, []);

  const openCreate = () => {
    setEditing('create');
    setForm({ name: '', category: 'men', manager_id: '', social_instagram: '', social_facebook: '', social_tiktok: '' });
  };
  const openEdit = (s) => {
    const sl = s.social_links || {};
    setEditing(s.id);
    setForm({
      name: s.name,
      category: s.category,
      manager_id: s.manager_id ?? '',
      social_instagram: sl.instagram || '',
      social_facebook: sl.facebook || '',
      social_tiktok: sl.tiktok || '',
    });
  };

  const save = async () => {
    try {
      const social_links = {};
      if (form.social_instagram?.trim()) social_links.instagram = form.social_instagram.trim();
      if (form.social_facebook?.trim()) social_links.facebook = form.social_facebook.trim();
      if (form.social_tiktok?.trim()) social_links.tiktok = form.social_tiktok.trim();
      const basePayload = {
        name: form.name,
        category: form.category,
        manager_id: form.manager_id ? parseInt(form.manager_id) : null,
      };
      const slPayload = Object.keys(social_links).length ? { social_links } : {};
      if (editing === 'create') await api.createAdminShop({ ...basePayload, ...slPayload });
      else await api.updateAdminShop(editing, { ...basePayload, ...slPayload });
      setEditing(null); fetchShops();
    } catch (err) { showAlert(err.message); }
  };

  const remove = async (id) => {
    showConfirm(t('admin.deleteSalonConfirm'), async () => {
      try { await api.deleteAdminShop(id); fetchShops(); }
      catch (err) { showAlert(err.message); }
    });
  };

  const managerLabel = (id) => { const u = users.find(x => x.id === id); return u ? u.name : (id ?? '—'); };
  const toggleBranches = (shopId) => setExpandedShop(prev => (prev === shopId ? null : shopId));

  const onShopLogoSelect = async (shopId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setUploadingShopLogo(shopId);
    try {
      await api.uploadShopLogo(shopId, formData);
      fetchShops();
    } catch (err) {
      showAlert(err.message || t('admin.uploadFailed'));
    } finally {
      setUploadingShopLogo(null);
      e.target.value = '';
    }
  };

  const onDeleteShopLogo = async (shopId) => {
    const shop = shops.find((s) => s.id === shopId);
    if (!shop?.logo_url) return;
    showConfirm(t('admin.deleteLogoConfirm', 'Delete this logo?'), async () => {
      setUploadingShopLogo(shopId);
      try {
        await api.deleteShopLogo(shopId);
        fetchShops();
      } catch (err) {
        showAlert(err.message || t('admin.uploadFailed'));
      } finally {
        setUploadingShopLogo(null);
      }
    });
  };

  return (
    <AdminLayout role="admin">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '2rem' }}>
        <div>
          <h1 style={S.pageTitle}>{t('admin.salons')}</h1>
          <div style={{ width: '40px', height: '2px', background: T.orange }} />
          <p style={{ fontSize: '0.78rem', color: T.muted, marginTop: '0.4rem' }}>{t('admin.salonsHint')}</p>
        </div>
        <button style={S.btnOrange} onClick={openCreate}
          onMouseEnter={e => e.target.style.background = T.orangeHov}
          onMouseLeave={e => e.target.style.background = T.orange}>
          + {t('admin.addSalon')}
        </button>
      </div>

      {/* Salon edit/create modal */}
      {editing && (
        <div style={S.modal}>
          <div style={S.modalBox}>
            <h2 style={S.modalTitle}>{editing === 'create' ? t('admin.newSalon') : t('admin.editSalon')}</h2>
            <input placeholder={t('admin.name')} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={S.input} />
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} style={S.select}>
              <option value="men">{t('admin.men')}</option>
              <option value="women">{t('admin.women')}</option>
              <option value="nails">{t('admin.nails')}</option>
            </select>
            <select value={form.manager_id} onChange={e => setForm({ ...form, manager_id: e.target.value })} style={S.select}>
              <option value="">{t('admin.noManager')}</option>
              {users.filter(u => u.role === 'shop_manager').map(u => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
            <div style={{ fontFamily: "'Oswald', sans-serif", fontSize: '0.72rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: T.muted, margin: '0.5rem 0 0.35rem' }}>{t('admin.salonSocialLinks')}</div>
            <input placeholder="Instagram URL" value={form.social_instagram} onChange={e => setForm({ ...form, social_instagram: e.target.value })} style={S.input} />
            <input placeholder="Facebook URL" value={form.social_facebook} onChange={e => setForm({ ...form, social_facebook: e.target.value })} style={S.input} />
            <input placeholder="TikTok URL" value={form.social_tiktok} onChange={e => setForm({ ...form, social_tiktok: e.target.value })} style={S.input} />
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={save} style={{ ...S.btnOrange, flex: 1 }}>{t('common.save')}</button>
              <button onClick={() => setEditing(null)} style={S.btnOutline}>{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {loading ? <p style={{ color: T.muted }}>{t('common.loading')}</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>
                {[t('admin.id'), t('admin.logo'), t('admin.name'), t('admin.category'), t('admin.manager'), t('admin.actions')].map((h, i) => (
                  <th key={i} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shops.map(s => (
                <>
                  {/* Salon row */}
                  <tr key={s.id}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(27,213,173,0.04)'}
                    onMouseLeave={e => e.currentTarget.style.background = expandedShop === s.id ? 'rgba(27,213,173,0.06)' : 'transparent'}
                    style={{ background: expandedShop === s.id ? 'rgba(27,213,173,0.06)' : 'transparent' }}>
                    <td style={S.td}>{s.id}</td>
                    <td style={S.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {s.logo_url ? (
                          <img src={resolveMediaUrl(s.logo_url)} alt="" loading="lazy" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 8, border: `1px solid ${T.border}` }} />
                        ) : (
                          <div style={{ width: 40, height: 40, borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: `1px dashed ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', color: T.muted }}>—</div>
                        )}
                        <label style={{ cursor: 'pointer', fontSize: '0.75rem', color: T.orange }}>
                          {uploadingShopLogo === s.id ? '…' : t('admin.upload')}
                          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => onShopLogoSelect(s.id, e)} />
                        </label>
                        {s.logo_url && (
                          <button type="button" onClick={() => onDeleteShopLogo(s.id)} style={{ ...S.btnDanger, padding: '0.2rem 0.45rem', fontSize: '0.68rem' }}>
                            {t('common.delete')}
                          </button>
                        )}
                      </div>
                    </td>
                    <td style={{ ...S.td, fontWeight: 600, color: T.white }}>{s.name}</td>
                    <td style={{ ...S.td, color: T.orange, fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', letterSpacing: '0.08em' }}>
                      {s.category}
                    </td>
                    <td style={S.td}>{managerLabel(s.manager_id)}</td>
                    <td style={S.td}>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {/* Toggle branches button */}
                        <button
                          style={{
                            ...S.btnSmall,
                            color: expandedShop === s.id ? T.orange : T.muted,
                            borderColor: expandedShop === s.id ? T.orange : T.border,
                          }}
                          onClick={() => toggleBranches(s.id)}
                          onMouseEnter={e => { e.target.style.color = T.orange; e.target.style.borderColor = T.orange; }}
                          onMouseLeave={e => {
                            e.target.style.color = expandedShop === s.id ? T.orange : T.muted;
                            e.target.style.borderColor = expandedShop === s.id ? T.orange : T.border;
                          }}
                        >
                          {expandedShop === s.id ? '▾' : '▸'} {t('admin.branches')}
                        </button>
                        <button style={S.btnSmall} onClick={() => openEdit(s)}
                          onMouseEnter={e => e.target.style.color = T.white}
                          onMouseLeave={e => e.target.style.color = T.muted}>{t('common.edit')}</button>
                        <button style={S.btnDanger} onClick={() => remove(s.id)}>{t('common.delete')}</button>
                      </div>
                    </td>
                  </tr>

                  {/* Expanded branches panel — full-width row below the shop */}
                  {expandedShop === s.id && (
                    <tr key={`branches-${s.id}`}>
                      <td colSpan={6} style={{ padding: 0, borderBottom: `1px solid ${T.border}` }}>
                        <BranchPanel shopId={s.id} allUsers={users} />
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

export default function Shops() {
  return <ProtectedRoute requireSuperAdmin><ShopsContent /></ProtectedRoute>;
}
